import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Spinner,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Stack, Text } from '@chakra-ui/layout';
import { useHistory } from 'react-router-dom';
import { FiTrash } from 'react-icons/all';
import { FiEdit } from 'react-icons/fi';
import { Button, Heading, Pagination, Table } from '~/components';
import { ROUTER_PATHS } from '~/constants';
import { deleteZeroCarbonLetter } from '~/services/ZeroCarbon';
import { ZeroCarbonLetter, ZeroCarbonLetterView } from '~/types/zeroCarbon';
import { queryClient } from '~/utils/react-query';
import { useZeroCarbonLetters } from '~/hooks/useZeroCarbon/useLetters';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';

export const ZeroCarbonLetters: React.VFC = () => {
  const history = useHistory();
  const toast = useToast();
  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();
  const [page, setPage] = useState(1);
  const [listaCartas, setListaCartas] = useState<ZeroCarbonLetterView[]>([]);
  const [cartaSelecionada, setCartaSelecionada] = useState<
    ZeroCarbonLetter | undefined
  >(undefined);
  const [isFetchingRemover, setIsFetchingRemover] = useState(false);
  const { data, isLoading, refetch } = useZeroCarbonLetters(page, 10, {
    onSuccess: r => {
      setListaCartas(r?.results || []);
    },
  });

  useEffect(() => {
    refetch();
  }, []);
  const removerCarta = (id: number) => {
    setIsFetchingRemover(true);
    deleteZeroCarbonLetter(id)
      .then(() => {
        toast({
          title: 'Carta removida com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        onCloseModal();
        if (listaCartas.length === 1 && page > 1) {
          setPage(page - 1);
          queryClient.invalidateQueries(['zero_carbon_letters', page - 1]);
        } else {
          queryClient.invalidateQueries(['zero_carbon_letters', page]);
        }
      })
      .catch(() => {
        toast({
          title: 'Erro ao remover carta',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetchingRemover(false);
        setCartaSelecionada(undefined);
      });
  };

  return (
    <Box px="8" py="4">
      <Stack gridGap={4}>
        <HStack justifyContent="space-between">
          <Heading>Carta Zero Carbon</Heading>
          <Button
            variant="primary"
            onClick={() =>
              history.push(ROUTER_PATHS.PORTAL.ADMIN.ZERO_CARBON_LETTERS_NEW)
            }
          >
            Cadastrar carta
          </Button>
        </HStack>
        <TableContainer>
          <Table type="secondary">
            <Thead>
              <Tr>
                <Th w="70%" maxW="70%">
                  NOME DO PROJETO
                </Th>
                <Th w="20%" maxW="20%">
                  ÚLTIMA VERSÃO
                </Th>
                <Th w="10%" maxW="10%" textAlign="center">
                  AÇÃO
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {listaCartas.length ? (
                listaCartas.map((carta, index) => (
                  <Tr key={carta.id}>
                    <Td>{carta.projeto || '-'}</Td>
                    <Td>{carta.formattedDataAtualizacao}</Td>
                    <Td textAlign="center">
                      <Tooltip hasArrow label="Editar carta" bg="black">
                        <IconButton
                          mr={3}
                          size="sm"
                          aria-label="Editar carta"
                          icon={<Icon as={FiEdit} />}
                          onClick={e => {
                            history.push(
                              `${ROUTER_PATHS.PORTAL.ADMIN.ZERO_CARBON_LETTERS}/${carta.id}`,
                            );
                          }}
                        />
                      </Tooltip>
                      <Tooltip hasArrow label="Remover carta" bg="black">
                        <IconButton
                          size="sm"
                          aria-label="Remover carta"
                          icon={<Icon as={FiTrash} />}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            setCartaSelecionada(carta);
                            onOpenModal();
                          }}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={7} textAlign="center">
                    {isLoading && <Spinner />}
                    {!isLoading && <Text>Nenhuma carta encontrada</Text>}
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
        <Flex justify="flex-end" height="fit-content">
          {data && (
            <Pagination
              marginTop="0px"
              totalCountOfRegisters={data.count}
              registersPerPage={10}
              currentPage={page}
              onPageChange={e => {
                setPage(e);
                queryClient.invalidateQueries(['zero_carbon_letters', e]);
              }}
            />
          )}
        </Flex>
      </Stack>
      <ConfirmationDialog
        confirmar={() => removerCarta(cartaSelecionada!.id)}
        onClose={onCloseModal}
        isOpen={isOpenModal}
        titulo="Remover carta"
        btnConfirmText="Confirmar"
        isDisabled={isFetchingRemover}
      >
        <span>Tem certeza que deseja remover a carta?</span>
      </ConfirmationDialog>
    </Box>
  );
};
